/* edit-definition-modal.tsx generated by @compiled/babel-plugin v0.38.1 */
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./edit-definition-modal.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useState } from 'react';
import debouncePromise from 'debounce-promise';
import { useIntl } from 'react-intl-next';
import Button from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Label } from '@atlaskit/form';
import ErrorIcon from '@atlaskit/icon/core/migration/status-error--error';
import Link from '@atlaskit/link';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Select, { AsyncSelect } from '@atlaskit/select';
import { setSessionItem } from '@atlassian/search-dialog';
import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { SEARCH_AI_ANSWER_CACHE_KEY } from '../../../../ai-answer-dialog/ai-answer-dialog';
import { onCuratedDefinitionUpdateButtonClicked } from '../../analytics';
import { updateDefinitionErrorPayload } from '../../error-analytics';
import { EditedDefinitionScope } from '../types';
import { MarkdownEditor } from './markdown-editor';
import { messages } from './messages';
import { RadialProgressBar } from './radial-progress-bar';
import { restrictionOptionFormatter } from './restriction-option-formatter';
import { SourceOption } from './source-option-formatter';
import { useCreateDefinitionSupplier } from './useCreateDefinitionSupplier';
import { useSearchSourceSupplier } from './useSearchSourceSupplier';
var MAX_DEFINITION_LENGTH = 500;
var editDefintionModalStyles = {
  editDefinitionTextboxLabelContainer: "_85i51b66",
  editDefinitionTextboxErrorMessageContainer: "_1q511b66",
  radialProgressBarContainer: "_85i5v77o",
  restrictionSelectSublineContainer: "_1q511b66"
};
export var EditDefinitionModal = function EditDefinitionModal(_ref) {
  var curatedDefinitionScopeConfig = _ref.curatedDefinitionScopeConfig,
    closeEditModal = _ref.closeEditModal,
    openFeedbackOptionsModal = _ref.openFeedbackOptionsModal,
    isCreateDefinition = _ref.isCreateDefinition,
    onSaveEditModal = _ref.onSaveEditModal;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    fireAnalyticsEvent = _useAIAnswerContext.fireAnalyticsEvent,
    commonAttributes = _useAIAnswerContext.commonAttributes,
    setData = _useAIAnswerContext.setData,
    answerEditor = _useAIAnswerContext.answerEditor,
    additionalQueryContext = _useAIAnswerContext.additionalQueryContext;
  var answerString = commonAttributes.answerString,
    keyPhrase = commonAttributes.query;
  var _useState = useState(answerString),
    _useState2 = _slicedToArray(_useState, 2),
    editedDefinition = _useState2[0],
    setEditedDefinition = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    charCount = _useState4[0],
    setCharCount = _useState4[1];
  var emptyDefinition = editedDefinition.trim() === '';
  var definitionOverMaxLength = charCount > MAX_DEFINITION_LENGTH;
  var updateDefinitionDisabled = emptyDefinition || definitionOverMaxLength;
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    sourceSearchInput = _useState6[0],
    setSourceSearchInput = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    source = _useState8[0],
    setSource = _useState8[1];
  var definitionRestrictionOptions = curatedDefinitionScopeConfig.definitionRestrictionOptions;
  var defaultRestrictionOption = definitionRestrictionOptions[0];
  var _useState9 = useState(defaultRestrictionOption.value),
    _useState0 = _slicedToArray(_useState9, 2),
    audienceRestriction = _useState0[0],
    setAudienceRestriction = _useState0[1];
  var _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    updateDefinitionLoading = _useState10[0],
    setUpdateDefinitionLoading = _useState10[1];
  var setDefinition = useCallback(function (value) {
    setEditedDefinition(value);
  }, [setEditedDefinition]);
  var _useSearchSourceSuppl = useSearchSourceSupplier(),
    searchSources = _useSearchSourceSuppl.searchSources;
  var onSourceSearchInputChange = function onSourceSearchInputChange(input, actionMeta) {
    if (actionMeta.action === 'input-change') {
      setSourceSearchInput(input);
    }
  };
  var onSelectSource = function onSelectSource(newValue, actionMeta) {
    if (actionMeta.action === 'select-option') {
      setSource(newValue);
    }
  };
  var onSelectRestrictionOption = function onSelectRestrictionOption(newValue, actionMeta) {
    if (actionMeta.action === 'select-option') {
      setAudienceRestriction((newValue === null || newValue === void 0 ? void 0 : newValue.value) || EditedDefinitionScope.ORGANIZATION);
    }
  };
  var _useCreateDefinitionS = useCreateDefinitionSupplier({
      curatedDefinitionScopeConfig: curatedDefinitionScopeConfig
    }),
    createDefinition = _useCreateDefinitionS.createDefinition;
  var onUpdateDefinition = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var updatedDefinition;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            setUpdateDefinitionLoading(true);
            _context.prev = 1;
            _context.next = 4;
            return createDefinition({
              keyPhrase: keyPhrase,
              editedDefinition: editedDefinition,
              scope: audienceRestriction,
              referenceContentId: source ? source.id : null,
              referenceUrl: source ? source.url : null
            });
          case 4:
            updatedDefinition = _context.sent;
            setUpdateDefinitionLoading(false);
            setSource(null);
            fireAnalyticsEvent(onCuratedDefinitionUpdateButtonClicked({
              commonAttributes: commonAttributes,
              newDefinition: editedDefinition,
              restrictedScope: audienceRestriction,
              sourceSelected: !!source,
              aiDefinitionEdited: !!!answerEditor
            }));
            setData(updatedDefinition);
            setSessionItem(SEARCH_AI_ANSWER_CACHE_KEY, JSON.stringify({
              query: keyPhrase,
              additionalContext: additionalQueryContext || '',
              data: updatedDefinition
            }));
            closeEditModal();
            onSaveEditModal === null || onSaveEditModal === void 0 || onSaveEditModal();
            _context.next = 18;
            break;
          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](1);
            fireAnalyticsEvent(updateDefinitionErrorPayload(_context.t0, commonAttributes));
            setUpdateDefinitionLoading(false);
          case 18:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[1, 14]]);
    }));
    return function onUpdateDefinition() {
      return _ref2.apply(this, arguments);
    };
  }();
  var conditionalSublineMessage;
  switch (audienceRestriction) {
    case EditedDefinitionScope.SPACE:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_space_subline;
        break;
      }
    case EditedDefinitionScope.PROJECT:
      {
        conditionalSublineMessage = FeatureGates.getExperimentValue('project-terminology-refresh', 'isEnabled', false) ? messages.edit_definition_restricted_to_project_subline_galaxia : messages.edit_definition_restricted_to_project_subline;
        break;
      }
    case EditedDefinitionScope.PAGE:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_page_subline;
        break;
      }
    case EditedDefinitionScope.BLOGPOST:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_space_subline;
        break;
      }
    case EditedDefinitionScope.ORGANIZATION:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_site_subline;
        break;
      }
    default:
      {
        conditionalSublineMessage = messages.edit_definition_restricted_to_site_subline;
        break;
      }
  }
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: closeEditModal,
    shouldScrollInViewport: true
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    hasCloseButton: true
  }, /*#__PURE__*/React.createElement(ModalTitle, null, formatMessage(isCreateDefinition ? messages.edit_definition_modal_header_create : messages.edit_definition_modal_header_edit))), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(Stack, {
    space: "space.150"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "p",
    color: "color.text.subtle"
  }, formatMessage(messages.edit_definition_modal_subheader_text), ' ', /*#__PURE__*/React.createElement(Link, {
    href: "https://support.atlassian.com/rovo/docs/add-or-edit-a-definition/"
  }, formatMessage(messages.edit_definition_modal_subheader_link_text))), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Box, {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    grow: "fill",
    spread: "space-between"
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: editDefintionModalStyles.editDefinitionTextboxLabelContainer
  }, /*#__PURE__*/React.createElement(Text, {
    color: "color.text.subtle",
    weight: "bold",
    size: "small"
  }, formatMessage(messages.edit_definition_text_box_label))), /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    space: "space.075"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    size: "small",
    color: "color.text.subtle"
  }, charCount, "/", MAX_DEFINITION_LENGTH), /*#__PURE__*/React.createElement(Box, {
    xcss: editDefintionModalStyles.radialProgressBarContainer
  }, /*#__PURE__*/React.createElement(RadialProgressBar, {
    current: charCount,
    max: MAX_DEFINITION_LENGTH
  }))))), /*#__PURE__*/React.createElement(MarkdownEditor, {
    value: editedDefinition,
    setValue: setDefinition,
    setCharCount: setCharCount,
    assistiveLabel: formatMessage(messages.edit_definition_text_box_label_with_count, {
      count: charCount,
      max: MAX_DEFINITION_LENGTH
    }),
    assistiveDescribedBy: "edit-definition-text-box-error-message"
  }), updateDefinitionDisabled && /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    space: "space.050",
    xcss: editDefintionModalStyles.editDefinitionTextboxErrorMessageContainer
  }, /*#__PURE__*/React.createElement(ErrorIcon, {
    label: "",
    LEGACY_size: "small",
    color: "var(--ds-text-danger, #AE2E24)",
    size: "small"
  }), /*#__PURE__*/React.createElement(Text, {
    color: "color.text.danger",
    size: "small",
    id: "edit-definition-text-box-error-message"
  }, emptyDefinition ? formatMessage(messages.edit_definition_no_definition) : formatMessage(messages.edit_definition_over_max_length, {
    max: MAX_DEFINITION_LENGTH
  })))), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Label, {
    htmlFor: "edit-definition-source-select"
  }, formatMessage(source ? messages.edit_definition_source_label : messages.edit_definition_add_source_label)), source ? /*#__PURE__*/React.createElement(SourceOption, {
    source: source,
    onClickSource: function onClickSource() {
      var _window$open;
      (_window$open = window.open(source.url, '_blank')) === null || _window$open === void 0 || _window$open.focus();
    },
    canClear: true,
    onRemoveSource: function onRemoveSource(e) {
      e.stopPropagation();
      setSource(null);
    }
  }) : /*#__PURE__*/React.createElement(AsyncSelect, {
    components: {
      DropdownIndicator: function DropdownIndicator() {
        return null;
      }
    },
    loadOptions: debouncePromise(function (input) {
      return searchSources(input);
    }, 500),
    formatOptionLabel: function formatOptionLabel(sourceOption) {
      return /*#__PURE__*/React.createElement(SourceOption, {
        source: sourceOption,
        canClear: false
      });
    },
    onChange: onSelectSource,
    inputValue: sourceSearchInput,
    onInputChange: onSourceSearchInputChange,
    placeholder: formatMessage(messages.edit_definition_source_search_box_placeholder),
    menuPosition: "fixed",
    inputId: "edit-definition-source-select"
  })), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Label, {
    htmlFor: "edit-definition-restriction-select"
  }, formatMessage(messages.edit_definition_restricted_to_label)), /*#__PURE__*/React.createElement(Select, {
    options: definitionRestrictionOptions,
    defaultValue: defaultRestrictionOption,
    onChange: onSelectRestrictionOption,
    formatOptionLabel: restrictionOptionFormatter,
    isSearchable: false,
    menuPosition: "fixed",
    inputId: "edit-definition-restriction-select",
    descriptionId: "edit-definition-restriction-subline"
  }), /*#__PURE__*/React.createElement(Box, {
    xcss: editDefintionModalStyles.restrictionSelectSublineContainer
  }, /*#__PURE__*/React.createElement(Text, {
    color: "color.text.subtle",
    size: "small"
  }, formatMessage(conditionalSublineMessage)))))), /*#__PURE__*/React.createElement(ModalFooter, null, openFeedbackOptionsModal && /*#__PURE__*/React.createElement(Button, {
    appearance: "subtle",
    onClick: function onClick() {
      closeEditModal();
      openFeedbackOptionsModal();
    }
  }, formatMessage(messages.edit_definition_back_button)), /*#__PURE__*/React.createElement(Button, {
    appearance: updateDefinitionDisabled ? 'default' : 'primary',
    isLoading: updateDefinitionLoading,
    onClick: onUpdateDefinition,
    isDisabled: updateDefinitionDisabled
  }, formatMessage(messages.edit_definition_update_button))));
};